import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { askSite, useSiteData } from "../lib/siteApi";
import { answerSiteQuestion } from "../lib/siteGuide";

const STARTERS = ["What services do you offer?", "Which projects are published?", "How do I contact RENERG?"];

function isLightColor(color) {
  const match = String(color || "").match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (!match) return null;
  const alpha = match[4] === undefined ? 1 : Number(match[4]);
  if (alpha < 0.35) return null;
  const red = Number(match[1]);
  const green = Number(match[2]);
  const blue = Number(match[3]);
  return (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255 > 0.72;
}

function toneOf(node) {
  const style = window.getComputedStyle(node);
  const fromColor = isLightColor(style.backgroundColor);
  if (fromColor !== null) return fromColor;
  const image = style.backgroundImage || "";
  if (!image || image === "none") return null;
  if (/rgba?\(\s*0\s*,\s*0\s*,\s*0|black|#000\b/.test(image)) return false;
  if (/rgba?\(\s*255\s*,\s*255\s*,\s*255|white|#fff\b/.test(image)) return true;
  if (image.includes("gradient") || image.includes("url(")) return false;
  return null;
}

function pageIsLight(x, y) {
  const stack = document.elementsFromPoint(x, y);
  for (const start of stack) {
    if (start.closest?.(".site-assistant")) continue;
    let node = start;
    while (node && node !== document.documentElement) {
      const light = toneOf(node);
      if (light !== null) return light;
      node = node.parentElement;
    }
  }
  return true;
}

export default function SiteAssistant() {
  const site = useSiteData();
  const rootRef = useRef(null);
  const [onLight, setOnLight] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Ask me about RENERG. I answer from the services, projects, and contact details published on this website.",
      links: [],
    },
  ]);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages, open]);

  useEffect(() => {
    const read = () => {
      const root = rootRef.current;
      if (!root) return;
      const box = root.getBoundingClientRect();
      const x = Math.min(window.innerWidth - 12, Math.max(12, box.right - 28));
      const y = Math.min(window.innerHeight - 12, Math.max(12, box.bottom - 22));
      const light = pageIsLight(x, y);
      setOnLight((current) => (current === light ? current : light));
    };
    read();
    window.addEventListener("scroll", read, { passive: true });
    window.addEventListener("resize", read);
    const timer = window.setInterval(read, 800);
    return () => {
      window.removeEventListener("scroll", read);
      window.removeEventListener("resize", read);
      window.clearInterval(timer);
    };
  }, [open]);

  async function ask(text) {
    const question = String(text || "").trim();
    if (!question || busy) return;
    setDraft("");
    setBusy(true);
    setMessages((current) => [...current, { role: "user", text: question, links: [] }]);
    const history = messages.filter((item) => item.role === "user" || item.role === "assistant").slice(-6);
    try {
      const answer = await askSite(question, history);
      setMessages((current) => [
        ...current,
        { role: "assistant", text: answer.reply, links: answer.links || [] },
      ]);
    } catch {
      const answer = answerSiteQuestion(site, question);
      setMessages((current) => [
        ...current,
        { role: "assistant", text: answer.reply, links: answer.links || [] },
      ]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div ref={rootRef} className={`site-assistant ${open ? "is-open" : "is-closed"} ${onLight ? "is-on-light" : "is-on-dark"}`}>
      {open ? (
        <section className="site-assistant-panel" aria-label="Ask about RENERG">
          <header className="site-assistant-head">
            <span className="site-assistant-mark" aria-hidden="true">AI</span>
            <div>
              <p className="site-assistant-kicker">RENERG</p>
              <h2>Ask about this site</h2>
            </div>
            <button type="button" className="site-assistant-close" onClick={() => setOpen(false)} aria-label="Close">
              ×
            </button>
          </header>
          <div className="site-assistant-log">
            {messages.map((message, index) => (
              <div key={`${message.role}-${index}`} className={`site-assistant-msg is-${message.role}`}>
                <p>{message.text}</p>
                {message.links?.length ? (
                  <div className="site-assistant-links">
                    {message.links.map((link) => (
                      <Link key={link.to + link.label} to={link.to} onClick={() => setOpen(false)}>
                        {link.label}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
            {busy ? <p className="site-assistant-wait">Writing an answer…</p> : null}
            <div ref={endRef} />
          </div>
          {messages.some((message) => message.role === "user") ? null : (
            <div className="site-assistant-starters">
              {STARTERS.map((item) => (
                <button key={item} type="button" onClick={() => ask(item)} disabled={busy}>
                  {item}
                </button>
              ))}
            </div>
          )}
          <form
            className="site-assistant-form"
            onSubmit={(event) => {
              event.preventDefault();
              ask(draft);
            }}
          >
            <label className="sr-only" htmlFor="site-assistant-input">Your question</label>
            <input
              id="site-assistant-input"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Ask about a service or project"
              autoComplete="off"
              disabled={busy}
            />
            <button type="submit" disabled={busy}>Send</button>
          </form>
        </section>
      ) : null}
      <button type="button" className="site-assistant-toggle" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        <span className="site-assistant-mark" aria-hidden="true">AI</span>
        {open ? "Close" : "Ask"}
      </button>
    </div>
  );
}
